// src/components/PreviewLaunch.tsx
import React, { ReactNode, useEffect } from "react";
import { useNotesStore, setOnboarded, CueNote } from "../state/notes-store";
import NoteBody from "./NoteBody";
import { Lightbulb, Tags, Info, Mic, Keyboard } from "lucide-react";
import Page from "./Page";
import PageTitle from "./PageTitle";
import TriggerTester from "./TriggerTester";
import "./components.css";

// NEW (refactor hooks)
import { useHints } from "../hooks/useHints";
import { usePacketName } from "../hooks/usePacketName";
import { usePracticeMode } from "../hooks/usePracticeMode";
import { usePageScroll } from "../hooks/usePageScroll";
import { useLocalStorageToggle } from "../hooks/useLocalStorageToggle";

// NEW (presentational)
import { ScrollFabs } from "./ScrollFabs";

// NEW: local-only practice mic + match drawer
import PracticeMicPanel from "./preview/PracticeMicPanel";
import PracticeMatchDrawer from "./preview/PracticeMatchDrawer";

interface PreviewLaunchProps {
  onTestTrigger: (text: string) => void;
}

/** ------------------------------
 * Constants
 * -------------------------------- */
const PREVIEW_HINT_KEYS = ["test", "readonly", "beforecall"] as const;
const HINT_PREFIX = "cueboard.hint.preview.";
const SHOW_KEYPHRASES_KEY = "cueboard.ui.showKeyPhrases";

/** ------------------------------
 * Local Hint (kept scoped to Preview)
 * -------------------------------- */
function Hint({
  id,
  children,
  style,
  className,
  compact = false,
  refreshToken = 0,
}: {
  id: (typeof PREVIEW_HINT_KEYS)[number];
  children: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  compact?: boolean;
  refreshToken?: number;
}) {
  const storageKey = `${HINT_PREFIX}${id}`;
  const computeVisible = () => {
    try { return localStorage.getItem(storageKey) !== "dismissed"; } catch { return true; }
  };
  const [visible, setVisible] = React.useState<boolean>(computeVisible);
  useEffect(() => { setVisible(computeVisible()); }, [refreshToken]);

  if (!visible) return null;
  const baseClasses = `hint ${compact ? "compact" : ""}`;
  return (
    <div className={`${baseClasses} ${className ?? ""}`} style={style} role="note" aria-live="polite">
      <Info size={16} className="hint-icon" aria-hidden />
      <div className="hint-content">{children}</div>
      <button
        type="button"
        aria-label="Dismiss hint"
        onClick={() => {
          try { localStorage.setItem(storageKey, "dismissed"); } catch {}
          setVisible(false);
        }}
        className="button button-ghost hint-dismiss"
      >
        ×
      </button>
    </div>
  );
}

/** ------------------------------
 * Component
 * -------------------------------- */
const PreviewLaunch: React.FC<PreviewLaunchProps> = ({ onTestTrigger }) => {
  const { notes } = useNotesStore();
  const typedNotes = notes as CueNote[];

  // Packet name (shared tiny hook)
  const { packetName } = usePacketName();

  // Hints (shared hook)
  const { hintsHidden, toggleHints, refreshToken: hintRefreshToken } = useHints(PREVIEW_HINT_KEYS, HINT_PREFIX);

  // Key phrase toggle (tiny generic hook)
  const [showKeyPhrases, toggleShowKeyPhrases] = useLocalStorageToggle(SHOW_KEYPHRASES_KEY, false);

  // Practice Mode (encapsulates mic/typed parity & highlight/scroll)
  const {
    mode, setMode,
    practiceTop, practiceToast,
    highlightedBoardId, highlightedTriggerText,
    testMsg, testKind,
    handlePracticeMatch,
    cardRefs
  } = usePracticeMode();

  // Page scroll ergonomics (reset to top on mount + jump arrows)
  const { showJumpUp, showJumpDown, jump } = usePageScroll({ stepRatio: 0.85, topResetOnMount: true });

  function onLaunch() {
    setOnboarded(true);
    window.location.href = "/live";
  }

  return (
    <Page>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <PageTitle>Preview &amp; Launch</PageTitle>

        {/* Right-side small controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Show key phrases toggle (shared with Tag) */}
          <label
            className="small muted"
            title="CueBoard listens for key phrases to surface your notes. You don’t need to manage these — but you can view them here if you’d like."
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <input
              type="checkbox"
              checked={showKeyPhrases}
              onChange={toggleShowKeyPhrases}
              aria-label="Show key phrases"
            />
            Show key trigger phrases
          </label>

          {/* Hints toggle */}
          <button
            className="button button-ghost"
            style={{ fontSize: 12, padding: "2px 6px" }}
            onClick={toggleHints}
            title={hintsHidden ? "Show Preview hints again" : "Hide Preview hints"}
          >
            {hintsHidden ? "Unhide Preview Hints" : "Hide Preview Hints"}
          </button>
        </div>
      </div>

      {/* Packet name subtitle */}
      <div className="muted" style={{ fontSize: 15, lineHeight: "20px", marginTop: -6, marginBottom: 12 }}>
        Prep Kit: <strong>{packetName || "Untitled Prep Kit"}</strong>
      </div>

      {/* Tester + Practice header */}
      <div className="card hoverable vstack-md" style={{ marginBottom: 12 }}>
        <div className="practice-tabs">
          <button
            className={`practice-tab ${mode === "typed" ? "active" : ""}`}
            onClick={() => setMode("typed")}
            aria-pressed={mode === "typed"}
            title="Type a phrase to test"
          >
            <Keyboard size={14} style={{ marginRight: 6 }} /> Typed Test
          </button>
          <button
            className={`practice-tab ${mode === "mic" ? "active" : ""}`}
            onClick={() => setMode("mic")}
            aria-pressed={mode === "mic"}
            title="Speak a phrase to test (Preview-only)"
          >
            <Mic size={14} style={{ marginRight: 6 }} /> Practice Mic (Preview-only)
          </button>

          {mode === "mic" && (
            <span className="practice-pill" aria-live="polite" role="status" title="Mic here never triggers cues in Live">
              Preview-only • Safe practice
            </span>
          )}
        </div>

        {mode === "typed" ? (
          <>
            <TriggerTester onTestTrigger={onTestTrigger} />
            <Hint id="test" compact refreshToken={hintRefreshToken}>
              <b>Test Trigger</b> — Try your trigger phrases two ways: by <b>typing</b> them above or <b>saying</b> them out loud using the Practice Mic.
              For best results, speak or type in <i>natural interview sentences</i> (e.g., <i>“Can you walk me through your background?”</i>).
              Short fragments may not fire on their own.
            </Hint>

            {testMsg && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  padding: "6px 8px",
                  borderRadius: 6,
                  background: testKind === "ok" ? "#e6ffed" : "#fff8e1",
                  border: "1px solid " + (testKind === "ok" ? "#b7ebc6" : "#ffe08a"),
                  display: "inline-block",
                }}
              >
                {testMsg}
              </div>
            )}
          </>
        ) : (
          <>
            <PracticeMicPanel onMatch={handlePracticeMatch} />
            <div className="subtle-divider" />
            <PracticeMatchDrawer top={practiceTop || null} />

            {/* Mic toast */}
            {practiceToast && (
              <div className={`practice-toast ${practiceToast.kind === "ok" ? "ok" : "warn"}`} role="status" aria-live="polite">
                {practiceToast.msg}
              </div>
            )}
          </>
        )}
      </div>

      {/* Hint: Read-only view */}
      <Hint id="readonly" compact style={{ marginBottom: 8 }} refreshToken={hintRefreshToken}>
        <b>Read-only view</b> — Preview shows your notes &amp; trigger phrases. <i>Go back to Tag to edit.</i>
      </Hint>

      {/* Notes grid */}
      <div className="notes-grid" style={{ gap: 20 }}>
        {typedNotes.map((note: CueNote, idx: number) => {
          const triggers = note.triggers ?? [];
          const isHighlighted = highlightedBoardId === note.id;
          return (
            <div
              key={note.id}
              ref={(el) => { cardRefs.current[note.id] = el; }}
              className={`card card-accent hoverable stack-sm cueboard-card ${isHighlighted ? "practice-highlight practice-pulse" : ""}`}
              aria-live={isHighlighted ? "polite" : undefined}
              aria-label={isHighlighted ? `Matched ${note.title}` : undefined}
            >
              <div className="muted small mb-1">#{idx + 1}</div>
              <h3 className="font-bold text-lg mb-1 flex items-center">
                <Lightbulb size={18} className="mr-2 text-yellow-500" />
                {note.title}
              </h3>
              <p className="muted small no-margin-top">CueBoard notes &amp; trigger phrases</p>
              <div className="note-body" tabIndex={-1} aria-readonly="true">
                <NoteBody text={note.body_markdown} />
              </div>
              <div className="mt-auto">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <label className="label flex items-center mb-1" style={{ marginBottom: 0 }}>
                    <Tags size={16} className="mr-2 text-gray-500" />
                    Trigger Phrases
                  </label>
                  {!showKeyPhrases && <span className="small muted" />}
                </div>

                {showKeyPhrases ? (
                  <div className="chip-wrap preview-readonly" role="list" aria-label="Trigger phrases (read-only)">
                    {triggers.map((t, i) => {
                      const emph = isHighlighted && highlightedTriggerText && normalizeInline(t) === normalizeInline(highlightedTriggerText);
                      return (
                        <div
                          key={`${note.id}-preview-chip-${i}`}
                          className={`chip ${emph ? "practice-trigger-ink" : ""}`}
                          role="listitem"
                          aria-disabled="true"
                          tabIndex={-1}
                        >
                          {t}
                        </div>
                      );
                    })}
                    {triggers.length === 0 && <div className="muted small">No triggers</div>}
                  </div>
                ) : (
                  <div className="small muted" style={{ marginTop: 6 }}>
                    <i>Key trigger phrases hidden (toggle above to view)</i>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating scroll helpers */}
      <ScrollFabs
        showUp={showJumpUp}
        showDown={showJumpDown}
        onUp={() => jump("up")}
        onDown={() => jump("down")}
      />

      {/* Hint: Before your call (above Start) */}
      <Hint id="beforecall" style={{ marginTop: 16, marginBottom: 8 }} refreshToken={hintRefreshToken}>
            <b>Before your call</b> — Click <b>Start</b>, wait for the <b>picker</b> to appear, then choose the screen or tab you’ll share. Make sure <b>'Also share system audio'</b> is enabled when prompted. <i>Do this <b>before</b> you join so CueBoard is listening and ready.</i>
               
                      <br /><br />
Safe to share: This won’t interfere with Zoom/Meet — you can still share slides or other screens in your call. Avoid double-sharing system audio.
        <div className="muted small" style={{ marginTop: 4 }}>
                </div>
        <div className="muted small" style={{ marginTop: 2 }}>   
In Live, press <b>1–9</b> to jump between boards instantly.
        </div>
      </Hint>

      <div className="actions justify-end">
        <button className="button button-primary" onClick={onLaunch}>Start Interview Session</button>
      </div>
    </Page>
  );
};

// tiny inline normalizer used only for chip emphasis equality
function normalizeInline(s: string): string {
  return (s || "").toLowerCase().normalize("NFKC").replace(/[^\p{L}\p{N}\s']/gu, " ").replace(/\s+/g, " ").trim();
}

export default PreviewLaunch;
